import { deriveReturnAbility, getPlayerRating } from '#shared';
import { teamOrderedRoster, teamReturnerPlayer } from '../team-roster.js';

type KickoffUnits = {
  kicker: any;
  coverUnit: any[];
  primaryReturner: any;
  secondaryReturner: any;
  frontLine: any[];
  wallLine: any[];
};

type PuntUnits = {
  punter: any;
  gunners: any[];
  protectors: any[];
  upback: any;
  returner: any;
  containPlayers: any[];
  shield: any;
  rushers: any[];
};

type OnsideUnits = {
  kicker: any;
  coverUnit: any[];
  handsUnit: any[];
};

type FieldGoalUnits = {
  kicker: any;
  holder: any;
  lineBlockers: any[];
  wings: any[];
  rushers: any[];
  edges: any[];
  leaper: any;
  returner: any;
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function playerRating(player: any, key: string, fallback: number): number {
  return clamp(getPlayerRating(player, key as any, fallback), 40, 99);
}

function identityKey(player: any): string | null {
  if (!player) return null;
  if (player.id != null) return String(player.id);
  if (player.playerId != null) return String(player.playerId);
  if (player.name) return `${player.name}:${player.number || ''}:${player.position || ''}`;
  return null;
}

/**
 * Builds a real, unique unit. Preferred specialists remain first, while the
 * ordered roster is the deterministic fallback for short or unusual rosters.
 * It intentionally never pads by repeating a player.
 */
function uniqueUnit(preferred: any[], roster: any[], count: number, excluded: any[] = []): any[] {
  const used = new Set(excluded.map(identityKey).filter(Boolean));
  const result: any[] = [];
  for (const player of [...preferred, ...roster]) {
    const key = identityKey(player);
    if (!player || !key || used.has(key)) continue;
    used.add(key);
    result.push(player);
    if (result.length === count) break;
  }
  return result;
}

function kickoffCoverScore(player: any): number {
  return playerRating(player, 'athleticism', 78) * 0.34
    + playerRating(player, 'physicality', 74) * 0.28
    + playerRating(player, 'physicality', 72) * 0.16
    + playerRating(player, 'precision', 70) * 0.1
    + playerRating(player, 'athleticism', 78) * 0.12;
}

function kickoffBlockScore(player: any): number {
  return playerRating(player, 'strength', 72) * 0.36
    + playerRating(player, 'strength', 70) * 0.24
    + playerRating(player, 'strength', 72) * 0.18
    + playerRating(player, 'athleticism', 72) * 0.12
    + playerRating(player, 'athleticism', 76) * 0.1;
}

function returnerScore(player: any): number {
  return deriveReturnAbility(player)
    + playerRating(player, 'athleticism', 75)
    + playerRating(player, 'athleticism', 74);
}

function findBackupReturner(team: any, primary: any): any {
  const roster = teamOrderedRoster(team);
  const preferred = roster
    .filter((player: any) => player?.id !== primary?.id && ['WR', 'RB', 'CB', 'S', 'FS', 'SS', 'TE'].includes(player?.position))
    .sort((left: any, right: any) => returnerScore(right) - returnerScore(left));
  return uniqueUnit(preferred, roster, 1, [primary])[0] || null;
}

function buildKickoffUnits(offense: any, defense: any): KickoffUnits {
  const offenseRoster = teamOrderedRoster(offense);
  const defenseRoster = teamOrderedRoster(defense);
  const kicker = offense.depthChart?.k
    || offenseRoster.find((player: any) => player?.position === 'K')
    || offenseRoster[0]
    || null;
  const coverPreferred = offenseRoster
    .filter((player: any) => player?.id !== kicker?.id && !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => kickoffCoverScore(right) - kickoffCoverScore(left));
  const coverUnit = uniqueUnit(coverPreferred, offenseRoster, 10, [kicker]);

  const primaryReturner = teamReturnerPlayer(defense, 'kick');
  const secondaryReturner = findBackupReturner(defense, primaryReturner);
  const blockPreferred = defenseRoster
    .filter((player: any) => ![primaryReturner?.id, secondaryReturner?.id].includes(player?.id) && !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => kickoffBlockScore(right) - kickoffBlockScore(left));
  const returnUnit = uniqueUnit(blockPreferred, defenseRoster, 9, [primaryReturner, secondaryReturner]);

  return {
    kicker,
    coverUnit,
    primaryReturner,
    secondaryReturner,
    frontLine: returnUnit.slice(0, 5),
    wallLine: returnUnit.slice(5, 9),
  };
}

function puntCoverageScore(player: any): number {
  return playerRating(player, 'athleticism', 77) * 0.34
    + playerRating(player, 'physicality', 74) * 0.26
    + playerRating(player, 'physicality', 74) * 0.16
    + playerRating(player, 'strength', 74) * 0.1
    + playerRating(player, 'athleticism', 78) * 0.14;
}

function puntBlockScore(player: any): number {
  return playerRating(player, 'strength', 72) * 0.32
    + playerRating(player, 'strength', 72) * 0.24
    + playerRating(player, 'strength', 74) * 0.16
    + playerRating(player, 'athleticism', 72) * 0.12
    + playerRating(player, 'athleticism', 76) * 0.16;
}

function buildPuntUnits(offense: any, defense: any): PuntUnits {
  const offenseRoster = teamOrderedRoster(offense);
  const defenseRoster = teamOrderedRoster(defense);
  const punter = offense.depthChart?.p
    || offenseRoster.find((player: any) => player?.position === 'P')
    || offenseRoster.find((player: any) => player?.position === 'K')
    || offenseRoster[0]
    || null;
  const coverPreferred = offenseRoster
    .filter((player: any) => player?.id !== punter?.id && !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => puntCoverageScore(right) - puntCoverageScore(left));
  const selectedCoverage = uniqueUnit(coverPreferred, offenseRoster, 10, [punter])
    .sort((left: any, right: any) => playerRating(right, 'athleticism', 76) - playerRating(left, 'athleticism', 76));
  const gunners = selectedCoverage.slice(0, 2);

  const returner = teamReturnerPlayer(defense, 'punt');
  const blockPreferred = defenseRoster
    .filter((player: any) => player?.id !== returner?.id && !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => puntBlockScore(right) - puntBlockScore(left));
  const containPlayers = [...blockPreferred]
    .sort((left: any, right: any) => playerRating(right, 'athleticism', 75) - playerRating(left, 'athleticism', 75))
    .slice(0, 2);
  const shield = blockPreferred.find((player) => !containPlayers.includes(player)) || null;
  const rushers = uniqueUnit(
    blockPreferred.filter((player) => !containPlayers.includes(player) && player !== shield),
    defenseRoster,
    7,
    [returner, ...containPlayers, shield],
  );

  return {
    punter,
    gunners,
    protectors: selectedCoverage.slice(2, 9),
    upback: selectedCoverage[9] || null,
    returner,
    containPlayers,
    shield,
    rushers,
  };
}

function recoveryRosterScore(player: any): number {
  return playerRating(player, 'precision', 72) * 0.46
    + playerRating(player, 'awareness', 72) * 0.34
    + playerRating(player, 'athleticism', 72) * 0.2
    + playerRating(player, 'strength', 70) * 0.08
    + playerRating(player, 'athleticism', 72) * 0.06;
}

function buildOnsideUnits(offense: any, defense: any): OnsideUnits {
  const offenseRoster = teamOrderedRoster(offense);
  const defenseRoster = teamOrderedRoster(defense);
  const kicker = offense.depthChart?.k
    || offenseRoster.find((player: any) => player?.position === 'K')
    || offenseRoster[0]
    || null;
  const coverPreferred = offenseRoster
    .filter((player: any) => player?.id !== kicker?.id && !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => recoveryRosterScore(right) - recoveryRosterScore(left));
  const handsPreferred = defenseRoster
    .filter((player: any) => !['K', 'P', 'QB'].includes(player?.position))
    .sort((left: any, right: any) => recoveryRosterScore(right) - recoveryRosterScore(left));
  return {
    kicker,
    coverUnit: uniqueUnit(coverPreferred, offenseRoster, 10, [kicker]),
    handsUnit: uniqueUnit(handsPreferred, defenseRoster, 11),
  };
}

function fieldGoalProtectScore(player: any): number {
  return playerRating(player, 'strength', 74) * 0.46
    + playerRating(player, 'strength', 74) * 0.22
    + playerRating(player, 'strength', 72) * 0.16
    + playerRating(player, 'athleticism', 78) * 0.16;
}

function fieldGoalRushScore(player: any): number {
  return playerRating(player, 'physicality', 74) * 0.48
    + playerRating(player, 'strength', 76) * 0.22
    + playerRating(player, 'athleticism', 74) * 0.14
    + playerRating(player, 'physicality', 72) * 0.06
    + playerRating(player, 'athleticism', 76) * 0.1;
}

function fieldGoalReturnerScore(player: any): number {
  return (deriveReturnAbility(player) || 76) * 0.34
    + playerRating(player, 'athleticism', 78) * 0.28
    + playerRating(player, 'athleticism', 76) * 0.2
    + playerRating(player, 'precision', 74) * 0.18;
}

function buildFieldGoalUnits(offense: any, defense: any): FieldGoalUnits {
  const offenseRoster = teamOrderedRoster(offense);
  const defenseRoster = teamOrderedRoster(defense);
  const kicker = offense.depthChart?.k
    || offenseRoster.find((player: any) => player?.position === 'K')
    || offenseRoster[0]
    || null;
  const holder = uniqueUnit([
    offense.depthChart?.p,
    ...offenseRoster.filter((player: any) => player?.position === 'P'),
    ...offenseRoster.filter((player: any) => player?.position === 'QB'),
  ], offenseRoster, 1, [kicker])[0] || null;
  const protectPreferred = offenseRoster
    .filter((player: any) => player?.id !== kicker?.id && player?.id !== holder?.id && !['K', 'P'].includes(player?.position))
    .sort((left: any, right: any) => fieldGoalProtectScore(right) - fieldGoalProtectScore(left));
  const protectionUnit = uniqueUnit(protectPreferred, offenseRoster, 9, [kicker, holder]);

  const returner = teamReturnerPlayer(defense, 'kick')
    || teamReturnerPlayer(defense, 'punt')
    || [...defenseRoster].sort((left: any, right: any) => fieldGoalReturnerScore(right) - fieldGoalReturnerScore(left))[0]
    || null;
  const rushPreferred = defenseRoster
    .filter((player: any) => player?.id !== returner?.id && !['K', 'P'].includes(player?.position))
    .sort((left: any, right: any) => fieldGoalRushScore(right) - fieldGoalRushScore(left));
  const rushUnit = uniqueUnit(rushPreferred, defenseRoster, 10, [returner]);

  return {
    kicker,
    holder,
    lineBlockers: protectionUnit.slice(0, 7),
    wings: protectionUnit.slice(7, 9),
    rushers: rushUnit.slice(0, 7),
    edges: rushUnit.slice(7, 9),
    leaper: rushUnit[9] || null,
    returner,
  };
}

function kickoffAssignments(units: KickoffUnits): { offense: Record<string, any>; defense: Record<string, any> } {
  return {
    offense: {
      KICKER: units.kicker,
      ...Object.fromEntries(units.coverUnit.map((player, index) => [`COVER${index + 1}`, player])),
    },
    defense: {
      ...Object.fromEntries(units.frontLine.map((player, index) => [`FRONT${index + 1}`, player])),
      ...Object.fromEntries(units.wallLine.map((player, index) => [`WALL${index + 1}`, player])),
      RETURNER1: units.primaryReturner,
      RETURNER2: units.secondaryReturner,
    },
  };
}

function puntAssignments(units: PuntUnits): { offense: Record<string, any>; defense: Record<string, any> } {
  return {
    offense: {
      ...Object.fromEntries(units.protectors.map((player, index) => [`PROTECT${index + 1}`, player])),
      UPBACK: units.upback,
      PUNTER: units.punter,
      GUNNER1: units.gunners[0],
      GUNNER2: units.gunners[1],
    },
    defense: {
      ...Object.fromEntries(units.rushers.map((player, index) => [`RUSH${index + 1}`, player])),
      CONTAIN1: units.containPlayers[0],
      CONTAIN2: units.containPlayers[1],
      SHIELD: units.shield,
      RETURNER: units.returner,
    },
  };
}

function onsideAssignments(units: OnsideUnits): { offense: Record<string, any>; defense: Record<string, any> } {
  return {
    offense: {
      KICKER: units.kicker,
      ...Object.fromEntries(units.coverUnit.map((player, index) => [`COVER${index + 1}`, player])),
    },
    defense: {
      ...Object.fromEntries(units.handsUnit.slice(0, 5).map((player, index) => [`FRONT${index + 1}`, player])),
      ...Object.fromEntries(units.handsUnit.slice(5, 9).map((player, index) => [`WALL${index + 1}`, player])),
      RETURNER1: units.handsUnit[9],
      RETURNER2: units.handsUnit[10],
    },
  };
}

function fieldGoalAssignments(units: FieldGoalUnits): { offense: Record<string, any>; defense: Record<string, any> } {
  return {
    offense: {
      ...Object.fromEntries(units.lineBlockers.map((player, index) => [`BLOCK${index + 1}`, player])),
      HOLDER: units.holder,
      KICKER: units.kicker,
      WING1: units.wings[0],
      WING2: units.wings[1],
    },
    defense: {
      ...Object.fromEntries(units.rushers.map((player, index) => [`RUSH${index + 1}`, player])),
      EDGE1: units.edges[0],
      EDGE2: units.edges[1],
      LEAPER1: units.leaper,
      LEAPER2: units.returner,
      RETURNER: units.returner,
    },
  };
}

export {
  buildFieldGoalUnits,
  buildKickoffUnits,
  buildOnsideUnits,
  buildPuntUnits,
  fieldGoalAssignments,
  kickoffAssignments,
  onsideAssignments,
  puntAssignments,
};
export type { FieldGoalUnits, KickoffUnits, OnsideUnits, PuntUnits };
